import { useEffect, useState } from "react";
import { Mail, Send, Loader2, CheckCircle2, MapPin, Clock, Navigation, Store } from "lucide-react";
import { GlassCard } from "../components/common/ui";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import api, { formatApiError } from "../lib/api";

const INFO_EMAIL = "info@clactonfireworks.co.uk";
const inp = "mt-1 bg-black/30 border-white/15 text-white";

function StoreInfo({ store }) {
  if (!store) return null;
  const addressLines = [store.address_line1, store.address_line2,
    [store.town, store.city_county].filter(Boolean).join(", "), store.postcode].filter(Boolean);
  const services = (store.services || []).filter((s) => s && s.name);
  return (
    <GlassCard className="mt-6 p-0 overflow-hidden" hover={false} data-testid="contact-store-info">
      <div className="p-6 sm:p-8">
        <div className="flex items-center gap-3 flex-wrap">
          {store.primary_logo && <img src={store.primary_logo} alt={`${store.name} logo`} className="h-12 w-12 rounded-lg object-contain bg-white p-1 border border-white/10" />}
          {store.secondary_logo && <img src={store.secondary_logo} alt={`${store.name} secondary logo`} className="h-12 w-12 rounded-lg object-contain bg-white p-1 border border-white/10" />}
          <div>
            {store.is_collection_point && <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-gold"><Store className="h-3.5 w-3.5" /> Collection Point</span>}
            <h2 className="font-playfair text-2xl text-white" data-testid="contact-store-name">{store.name}</h2>
          </div>
        </div>

        <div className="mt-5 grid sm:grid-cols-2 gap-6 text-sm">
          {addressLines.length > 0 && (
            <div className="flex items-start gap-3" data-testid="contact-store-address">
              <MapPin className="h-4 w-4 text-gold mt-0.5 shrink-0" aria-hidden="true" />
              <div className="text-white/80">{addressLines.map((l, i) => <div key={i}>{l}</div>)}</div>
            </div>
          )}
          {store.opening_hours_display?.length > 0 && (
            <div className="flex items-start gap-3" data-testid="contact-store-hours">
              <Clock className="h-4 w-4 text-gold mt-0.5 shrink-0" aria-hidden="true" />
              <div className="text-white/80">{store.opening_hours_display.map((h, i) => <div key={i}>{h}</div>)}</div>
            </div>
          )}
        </div>

        {store.directions_url && (
          <a href={store.directions_url} target="_blank" rel="noopener noreferrer" className="inline-block mt-5" data-testid="contact-directions-btn">
            <Button className="cf-gold-btn font-semibold gap-2"><Navigation className="h-4 w-4" /> Get Directions</Button>
          </a>
        )}

        {services.length > 0 && (
          <div className="mt-6" data-testid="contact-store-services">
            <p className="text-[11px] uppercase tracking-wider text-white/50">Store services</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {services.map((sv, i) => (
                <span key={i} className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-sm text-white/85">
                  {sv.image && <img src={sv.image} alt="" className="h-5 w-5 rounded-sm object-contain bg-white" />}
                  {sv.name}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {store.map_embed_url && (
        <iframe
          title={`Map — ${store.name}`}
          src={store.map_embed_url}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="w-full h-[320px] border-0 border-t border-white/10"
          data-testid="contact-store-map"
        />
      )}
    </GlassCard>
  );
}

export default function Contact() {
  const [page, setPage] = useState(null);
  const [store, setStore] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    document.title = "Contact Us · Clacton Fireworks";
    api.get("/content/pages/contact").then(({ data }) => setPage(data)).catch(() => setPage(null));
    api.get("/stores/primary").then(({ data }) => setStore(data)).catch(() => setStore(null));
    return () => { document.title = "Clacton Fireworks"; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError("Please fill in your name, email and message."); return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-20" data-testid="contact-page">
      <h1 className="font-playfair text-3xl sm:text-4xl text-white">{page?.title || "Contact Us"}</h1>

      <GlassCard className="mt-6 p-5 flex items-center gap-3" hover={false}>
        <div className="grid place-items-center h-11 w-11 rounded-xl bg-gold/10 border border-gold/20 shrink-0">
          <Mail className="h-5 w-5 text-gold" />
        </div>
        <div>
          <p className="text-sm text-white/60">Email us</p>
          <a href={`mailto:${INFO_EMAIL}`} className="text-gold font-medium hover:underline" data-testid="contact-info-email">{INFO_EMAIL}</a>
        </div>
      </GlassCard>

      <StoreInfo store={store} />

      <GlassCard className="mt-6 p-8" hover={false}>
        {done ? (
          <div className="text-center py-8" data-testid="contact-success">
            <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto" />
            <p className="mt-4 font-playfair text-2xl text-white">Message received</p>
            <p className="mt-2 text-muted-foreground">Thank you — we will contact you soon. A confirmation email is on its way to you.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-white/80">Name *</Label><Input data-testid="contact-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inp} /></div>
              <div><Label className="text-white/80">Email *</Label><Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inp} /></div>
            </div>
            <div><Label className="text-white/80">Phone (optional)</Label><Input data-testid="contact-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inp} /></div>
            <div><Label className="text-white/80">Message *</Label><Textarea data-testid="contact-message" value={form.message} onChange={(e) => set("message", e.target.value)} rows={6} className={inp} /></div>
            {error && <p className="text-sm text-firered" data-testid="contact-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="contact-submit" className="cf-gold-btn font-semibold w-full gap-2 h-12">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send Message
            </Button>
          </form>
        )}
      </GlassCard>
    </div>
  );
}
